<template>
  <div class="account-management">
    <!-- 页面标题和操作按钮 -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">用户管理</h1>
          <p class="page-subtitle">管理系统用户和权限</p>
        </div>
        <div class="action-buttons">
          <button class="btn btn-outline" @click="showRoleManagement">
            <a-icon type="safety" />
            角色管理
          </button>
          <button class="btn btn-outline" @click="showDepartmentManagement">
            <a-icon type="apartment" />
            部门管理
          </button>
          <button class="btn btn-primary" @click="showAddUser">
            <a-icon type="plus" />
            添加用户
          </button>
          <button class="btn btn-danger" @click="clearLoginState" style="margin-left: 8px;">
            <a-icon type="logout" />
            清除登录状态
          </button>
        </div>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <div class="search-section">
      <div class="search-bar">
        <a-input
          v-model="searchKeyword"
          placeholder="搜索用户姓名或邮箱..."
          class="search-input"
        >
          <a-icon slot="prefix" type="search" />
        </a-input>
        <a-select
          v-model="selectedRole"
          placeholder="所有角色"
          class="role-filter"
          allowClear
        >
          <a-select-option value="">所有角色</a-select-option>
          <a-select-option value="super_admin">超级管理员</a-select-option>
          <a-select-option value="admin">管理员</a-select-option>
          <a-select-option value="sales">销售</a-select-option>
          <a-select-option value="bd">BD</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 用户列表 -->
    <div class="user-list-section">
      <div class="list-header">
        <h3>用户列表</h3>
        <span class="user-count">共 {{ filteredUsers.length }} 个用户</span>
      </div>
      
      <div class="user-table">
        <div class="table-header">
          <div class="header-cell">用户信息</div>
          <div class="header-cell">角色</div>
          <div class="header-cell">部门</div>
          <div class="header-cell">状态</div>
          <div class="header-cell">权限</div>
          <div class="header-cell">创建时间</div>
          <div class="header-cell">操作</div>
        </div>
        
        <div class="table-body">
          <div 
            v-for="user in filteredUsers" 
            :key="user.id" 
            class="table-row"
          >
            <div class="table-cell user-info">
              <div class="user-avatar">
                <a-icon type="user" />
              </div>
              <div class="user-details">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-email">{{ user.email }}</div>
              </div>
            </div>
            
            <div class="table-cell role-cell">
              <span class="role-badge">{{ user.role }}</span>
            </div>
            
            <div class="table-cell department-cell">
              <span class="department-name">{{ user.department }}</span>
            </div>
            
            <div class="table-cell status-cell">
              <span class="status-badge" :class="user.status === 'active' ? 'active' : 'inactive'">
                {{ user.status === 'active' ? '激活' : '未激活' }}
              </span>
            </div>
            
            <div class="table-cell permissions-cell">
              <div class="permissions-list">
                <span 
                  v-for="(permission, index) in user.permissions.slice(0, 2)" 
                  :key="index"
                  class="permission-tag"
                >
                  {{ permission }}
                </span>
                <span v-if="user.permissions.length > 2" class="more-permissions">
                  +{{ user.permissions.length - 2 }}
                </span>
              </div>
            </div>
            
            <div class="table-cell date-cell">
              {{ user.createTime }}
            </div>
            
            <div class="table-cell actions-cell">
              <button class="action-btn edit-btn" @click="editUser(user)">
                <a-icon type="edit" />
              </button>
              <button class="action-btn delete-btn" @click="deleteUser(user)">
                <a-icon type="delete" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加用户模态框 -->
    <a-modal
      v-model="addUserVisible"
      title="添加新用户"
      :footer="null"
      width="500px"
      class="add-user-modal"
    >
      <div class="modal-content">
        <p class="modal-subtitle">创建新的系统用户账户</p>
        
        <a-form :form="addUserForm" layout="vertical">
          <a-form-item label="姓名">
            <a-input
              v-decorator="['name', { rules: [{ required: true, message: '请输入用户姓名' }] }]"
              placeholder="请输入用户姓名"
            />
          </a-form-item>
          
          <a-form-item label="邮箱">
            <a-input
              v-decorator="['email', { rules: [{ required: true, message: '请输入邮箱地址' }] }]"
              placeholder="请输入邮箱地址"
            />
          </a-form-item>
          
          <a-form-item label="角色">
            <a-select
              v-decorator="['role', { rules: [{ required: true, message: '请选择角色' }] }]"
              placeholder="选择角色"
            >
              <a-select-option value="super_admin">超级管理员</a-select-option>
              <a-select-option value="admin">管理员</a-select-option>
              <a-select-option value="sales">销售</a-select-option>
              <a-select-option value="bd">BD</a-select-option>
            </a-select>
          </a-form-item>
          
          <a-form-item label="部门">
            <a-input
              v-decorator="['department', { rules: [{ required: true, message: '请输入部门名称' }] }]"
              placeholder="请输入部门名称"
            />
          </a-form-item>
        </a-form>
        
        <div class="modal-actions">
          <button class="btn btn-outline" @click="addUserVisible = false">取消</button>
          <button class="btn btn-primary" @click="handleAddUser">添加用户</button>
        </div>
      </div>
    </a-modal>

    <!-- 角色管理模态框 -->
    <a-modal
      v-model="roleManagementVisible"
      title="角色权限管理"
      :footer="null"
      width="800px"
      class="role-management-modal"
    >
      <div class="modal-content">
        <p class="modal-subtitle">管理系统角色和权限配置</p>
        
        <div class="role-section">
          <div class="section-header">
            <h4>角色列表</h4>
            <button class="btn btn-primary" @click="showAddRole">
              <a-icon type="plus" />
              添加角色
            </button>
          </div>
          
          <div class="role-table">
            <div class="table-header">
              <div class="header-cell">角色名称</div>
              <div class="header-cell">描述</div>
              <div class="header-cell">权限数量</div>
              <div class="header-cell">用户数量</div>
              <div class="header-cell">创建时间</div>
              <div class="header-cell">操作</div>
            </div>
            
            <div class="table-body">
              <div v-if="roleLoading" class="loading-row">
                <div class="loading-text">正在加载角色数据...</div>
              </div>
              <div v-else-if="roles.length === 0" class="empty-row">
                <div class="empty-text">暂无角色数据</div>
              </div>
              <template v-else>
                <div 
                  v-for="role in roles" 
                  :key="role.id" 
                  class="table-row"
                >
                <div class="table-cell">{{ role.name }}</div>
                <div class="table-cell">{{ role.description }}</div>
                <div class="table-cell">{{ role.permissionCount }}个权限</div>
                <div class="table-cell">{{ role.userCount }}个用户</div>
                <div class="table-cell">{{ role.createTime }}</div>
                <div class="table-cell">
                  <button class="action-btn edit-btn" @click="editRole(role)">
                    <a-icon type="edit" />
                  </button>
                  <button class="action-btn delete-btn" @click="deleteRole(role)">
                    <a-icon type="delete" />
                  </button>
                </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        
        <div class="modal-actions">
          <button class="btn btn-outline" @click="roleManagementVisible = false">关闭</button>
        </div>
      </div>
    </a-modal>

    <!-- 部门管理模态框 -->
    <a-modal
      v-model="departmentManagementVisible"
      title="部门管理"
      :footer="null"
      width="1200px"
      class="department-management-modal"
      :bodyStyle="{ padding: '0', height: '600px' }"
    >
      <div class="department-modal-content">
        <div class="modal-header">
          <div class="header-info">
            <h3 class="modal-title">部门管理</h3>
            <p class="modal-subtitle">管理部门和成员配置，支持部门层级管理和人员分配</p>
          </div>
          <div class="header-actions">
            <button class="btn btn-outline" @click="refreshDepartments">
              <a-icon type="reload" />
              刷新
            </button>
            <button class="btn btn-primary" @click="showAddDepartment">
              <a-icon type="plus" />
              添加部门
            </button>
          </div>
        </div>
        
        <div class="department-management-layout">
          <div class="department-list-section">
            <div class="section-header">
              <h4>部门列表</h4>
              <div class="section-stats">
                <span class="stat-item">共 {{ departments.length }} 个部门</span>
              </div>
            </div>
            
            <div class="department-table-container">
              <div class="department-table">
                <div class="table-header">
                  <div class="header-cell">部门编码</div>
                  <div class="header-cell">部门名称</div>
                  <div class="header-cell">负责人</div>
                  <div class="header-cell">成员数</div>
                  <div class="header-cell">创建时间</div>
                  <div class="header-cell">操作</div>
                </div>
                
                <div class="table-body" v-if="!departmentLoading">
                  <div 
                    v-for="department in departments" 
                    :key="department.id" 
                    class="table-row"
                    :class="{ active: selectedDepartment && selectedDepartment.id === department.id }"
                    @click="selectDepartment(department)"
                  >
                    <div class="table-cell dept-code">
                      <span class="code-badge">{{ department.dept_code }}</span>
                    </div>
                    <div class="table-cell dept-info">
                      <div class="department-info">
                        <div class="department-name">{{ department.name }}</div>
                        <div class="department-desc">{{ department.description }}</div>
                      </div>
                    </div>
                    <div class="table-cell dept-leader">
                      <span class="leader-name">{{ department.leader }}</span>
                    </div>
                    <div class="table-cell dept-members">
                      <span class="member-count">{{ department.memberCount }}</span>
                    </div>
                    <div class="table-cell dept-time">
                      <span class="create-time">{{ department.createTime || '2024-01-01' }}</span>
                    </div>
                    <div class="table-cell dept-actions">
                      <div class="action-buttons">
                        <button class="action-btn view-btn" @click.stop="viewDepartmentMembers(department)" title="查看成员">
                          <a-icon type="team" />
                        </button>
                        <button class="action-btn edit-btn" @click.stop="editDepartment(department)" title="编辑部门">
                          <a-icon type="edit" />
                        </button>
                        <button class="action-btn delete-btn" @click.stop="deleteDepartment(department)" title="删除部门">
                          <a-icon type="delete" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="departmentLoading" class="loading-state">
                  <a-spin size="large" />
                  <p>加载部门数据中...</p>
                </div>
                
                <div v-if="!departmentLoading && departments.length === 0" class="empty-state">
                  <a-icon type="apartment" />
                  <p>暂无部门数据</p>
                  <button class="btn btn-primary" @click="showAddDepartment">
                    <a-icon type="plus" />
                    添加第一个部门
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="department-members-section">
            <div class="members-section-header">
              <div class="members-title">
                <h4>部门成员</h4>
                <span v-if="selectedDepartment" class="dept-name">{{ selectedDepartment.name }}</span>
              </div>
              <div class="members-actions" v-if="selectedDepartment">
                <button class="btn btn-outline btn-sm" @click="addDepartmentMember">
                  <a-icon type="plus" />
                  添加成员
                </button>
                <button class="btn btn-outline btn-sm" @click="exportDepartmentMembers">
                  <a-icon type="download" />
                  导出
                </button>
              </div>
            </div>
            
            <div v-if="!selectedDepartment" class="empty-state">
              <div class="empty-icon">
                <a-icon type="team" />
              </div>
              <h5>选择部门查看成员</h5>
              <p>请从左侧选择一个部门来查看其成员信息</p>
            </div>
            
            <div v-else class="members-content">
              <div v-if="employeeLoading" class="loading-state">
                <a-spin />
                <p>加载员工数据中...</p>
              </div>
              
              <div v-else-if="employees.length === 0" class="empty-state">
                <div class="empty-icon">
                  <a-icon type="user" />
                </div>
                <h5>该部门暂无员工</h5>
                <p>点击"添加成员"按钮为该部门添加员工</p>
                <button class="btn btn-primary" @click="addDepartmentMember">
                  <a-icon type="plus" />
                  添加成员
                </button>
              </div>
              
              <div v-else class="members-list">
                <div class="members-stats">
                  <span class="stat-item">共 {{ employees.length }} 名员工</span>
                  <span class="stat-item">在职 {{ employees.filter(emp => emp.status === '在职').length }} 人</span>
                </div>
                
                <div class="members-grid">
                  <div 
                    v-for="member in employees" 
                    :key="member.id"
                    class="member-card"
                    @click="editEmployee(member)"
                  >
                    <div class="member-avatar">
                      <a-icon type="user" />
                    </div>
                    <div class="member-info">
                      <div class="member-name">{{ member.name }}</div>
                      <div class="member-role">{{ member.role }}</div>
                      <div class="member-phone">{{ member.phone }}</div>
                      <div class="member-status" :class="member.status === '在职' ? 'active' : 'inactive'">
                        {{ member.status }}
                      </div>
                    </div>
                    <div class="member-actions">
                      <button class="action-btn edit-btn" @click.stop="editEmployee(member)" title="编辑员工">
                        <a-icon type="edit" />
                      </button>
                      <button class="action-btn delete-btn" @click.stop="removeEmployee(member)" title="移除员工">
                        <a-icon type="delete" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-actions">
          <button class="btn btn-outline" @click="departmentManagementVisible = false">关闭</button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'SystemManagement',
  data() {
    return {
      searchKeyword: '',
      selectedRole: '',
      addUserVisible: false,
      roleManagementVisible: false,
      departmentManagementVisible: false,
      selectedDepartment: null,
      addUserForm: this.$form.createForm(this),
      
      // 模拟用户数据
      users: [
        {
          id: 1,
          name: '张三',
          email: 'zhangsan@inly.com',
          role: '超级管理员',
          department: '管理部',
          status: 'active',
          permissions: ['用户管理', '角色管理', '项目管理', '数据查看', '系统设置', '日志管理'],
          createTime: '2024-01-15'
        },
        {
          id: 2,
          name: '李四',
          email: 'lisi@inly.com',
          role: '管理员',
          department: '运营部',
          status: 'active',
          permissions: ['项目管理', '达人管理', '数据查看'],
          createTime: '2024-01-20'
        },
        {
          id: 3,
          name: '王五',
          email: 'wangwu@inly.com',
          role: '销售',
          department: '销售部',
          status: 'active',
          permissions: ['项目管理', '数据查看'],
          createTime: '2024-02-01'
        }
      ],
      
      // 角色数据
      roles: [],
      roleLoading: false,
      
      // 部门数据 - 与现有部门管理接口保持一致
      departments: [],
      departmentLoading: false,
      
      // 员工数据 - 与现有员工管理接口保持一致
      employees: [],
      employeeLoading: false,
      selectedDeptCode: null
    };
  },
  
  mounted() {
    // 初始化时加载部门数据
    this.loadDepartments();
  },
  
  computed: {
    filteredUsers() {
      let filtered = this.users;
      
      if (this.searchKeyword) {
        filtered = filtered.filter(user => 
          user.name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
          user.email.toLowerCase().includes(this.searchKeyword.toLowerCase())
        );
      }
      
      if (this.selectedRole) {
        filtered = filtered.filter(user => user.role === this.selectedRole);
      }
      
      return filtered;
    }
  },
  
  methods: {
    // 清除登录状态 - 用于测试
    clearLoginState() {
      this.$confirm({
        title: '确认清除',
        content: '确定要清除登录状态吗？这将跳转到登录页。',
        onOk: () => {
          console.log('手动清除登录状态');
          this.$store.commit('setLoginOut');
          this.$router.push('/login');
        }
      });
    },
    
    showAddUser() {
      this.addUserVisible = true;
    },
    
    showRoleManagement() {
      this.roleManagementVisible = true;
      this.loadRoles();
    },
    
    // 加载角色数据
    loadRoles() {
      this.roleLoading = true;
      console.log('=== 开始加载角色数据 ===');
      this.axios.get('web/role/roleList', {
        params: {
          page: 0,
          size: 100
        }
      }).then((res) => {
        console.log('角色接口返回数据:', res.data);
        if (res.data && res.data.records) {
          this.roles = res.data.records.map(role => ({
            ...role,
            permissionCount: role.permissionCount || Math.floor(Math.random() * 10) + 1,
            userCount: role.userCount || Math.floor(Math.random() * 5) + 1
          }));
          console.log('处理后的角色数据:', this.roles);
        } else {
          this.roles = [];
        }
        this.roleLoading = false;
      }).catch((error) => {
        console.error('角色接口调用失败:', error);
        this.roles = [];
        this.roleLoading = false;
      });
    },
    
    showDepartmentManagement() {
      this.departmentManagementVisible = true;
      this.loadDepartments();
    },
    
    handleAddUser() {
      this.addUserForm.validateFields((err, values) => {
        if (!err) {
          // 这里应该调用API添加用户
          console.log('添加用户:', values);
          this.$message.success('用户添加成功');
          this.addUserVisible = false;
          this.addUserForm.resetFields();
        }
      });
    },
    
    editUser(user) {
      console.log('编辑用户:', user);
      this.$message.info('编辑用户功能待实现');
    },
    
    deleteUser(user) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除用户 ${user.name} 吗？`,
        onOk: () => {
          console.log('删除用户:', user);
          this.$message.success('用户删除成功');
        }
      });
    },
    
    showAddRole() {
      this.$message.info('添加角色功能待实现');
    },
    
    editRole(role) {
      console.log('编辑角色:', role);
      this.$message.info('编辑角色功能待实现');
    },
    
    deleteRole(role) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除角色 ${role.name} 吗？`,
        onOk: () => {
          console.log('删除角色:', role);
          this.$message.success('角色删除成功');
        }
      });
    },
    
    // 获取部门列表 - 使用现有部门管理接口
    loadDepartments() {
      this.departmentLoading = true;
      console.log('=== 开始加载部门数据 ===');
      this.axios.get('web/department/getDeptList', {
        params: {
          page: 0,
          size: 1000
        }
      }).then((res) => {
        console.log('部门接口返回数据:', res.data);
        // 检查业务状态码
        if (res.data && res.data.code === 9006) {
          console.log('部门接口返回9006错误，直接处理认证失败');
          console.log('当前登录状态:', this.$store.state.isLogin);
          
          // 直接清除登录状态并跳转
          this.$store.commit('setLoginOut');
          this.$message.warning(res.data.msg || '登录已过期，请重新登录');
          
          // 跳转到登录页
          setTimeout(() => {
            this.$router.push('/login');
          }, 100);
          
          return Promise.reject(new Error('认证失败'));
        }
        // 检查数据结构 - 数据在 res.data.data.records 中
        if (res.data && res.data.data && res.data.data.records) {
          this.departments = res.data.data.records.map(dept => ({
            id: dept.dept_code,
            dept_code: dept.dept_code,
            name: dept.dept_name,
            description: dept.dept_glbdef3_desc || '部门描述',
            leader: dept.principal || '未设置',
            memberCount: 0, // 需要单独计算
            members: []
          }));
          console.log('处理后的部门数据:', this.departments);
          console.log('部门总数:', res.data.data.total);
        } else {
          console.log('部门接口返回数据为空或格式不正确:', res.data);
          console.log('数据结构检查:', {
            hasData: !!res.data,
            hasDataData: !!(res.data && res.data.data),
            hasRecords: !!(res.data && res.data.data && res.data.data.records)
          });
          this.departments = [];
        }
        this.departmentLoading = false;
      }).catch((error) => {
        console.error('部门接口调用失败:', error);
        this.departmentLoading = false;
        
        // 如果是认证失败，不要显示错误消息，让拦截器处理
        if (error.message === '认证失败') {
          console.log('认证失败，由拦截器处理跳转');
          return;
        }
        
        this.$message.error('获取部门列表失败');
      });
    },
    
    // 获取部门员工列表 - 使用现有员工管理接口
    loadDepartmentEmployees(deptCode) {
      this.employeeLoading = true;
      this.axios.post('web/staff/queryStaff', {
        deptCode: deptCode,
        pageNo: 1,
        pageSize: 1000
      }).then(({ data: res }) => {
        if (res.data && res.data.records) {
          this.employees = res.data.records.map(emp => ({
            id: emp.code,
            name: emp.name,
            role: emp.work_nature || '员工',
            phone: emp.tel_num,
            status: emp.status || '在职'
          }));
          
          // 更新选中部门的成员数据
          if (this.selectedDepartment) {
            this.selectedDepartment.members = this.employees;
            this.selectedDepartment.memberCount = this.employees.length;
          }
        } else {
          this.employees = [];
        }
        this.employeeLoading = false;
      }).catch(() => {
        this.employeeLoading = false;
        this.$message.error('获取员工列表失败');
      });
    },
    
    selectDepartment(department) {
      this.selectedDepartment = department;
      this.selectedDeptCode = department.dept_code;
      this.loadDepartmentEmployees(department.dept_code);
    },
    
    showAddDepartment() {
      this.$message.info('添加部门功能待实现');
    },
    
    viewDepartmentMembers(department) {
      this.selectDepartment(department);
    },
    
    editDepartment(department) {
      console.log('编辑部门:', department);
      this.$message.info('编辑部门功能待实现');
    },
    
    deleteDepartment(department) {
      if (department.dept_code === '10000') {
        this.$message.warning('初始单位部门不允许删除！');
        return;
      }
      
      this.$confirm({
        title: '确认删除',
        content: `确定要删除部门 ${department.name} 吗？`,
        onOk: () => {
          this.axios.post(`web/department/delDept/${department.dept_code}`).then(() => {
            this.$message.success('部门删除成功');
            this.loadDepartments();
          }).catch(() => {
            this.$message.error('删除部门失败');
          });
        }
      });
    },
    
    addDepartmentMember() {
      this.$message.info('添加部门成员功能待实现');
    },
    
    refreshDepartments() {
      this.loadDepartments();
    },
    
    exportDepartmentMembers() {
      if (!this.selectedDepartment) {
        this.$message.warning('请先选择一个部门');
        return;
      }
      this.$message.info('导出部门成员功能待实现');
    },
    
    editEmployee(employee) {
      console.log('编辑员工:', employee);
      this.$message.info('编辑员工功能待实现');
    },
    
    removeEmployee(employee) {
      this.$confirm({
        title: '确认移除',
        content: `确定要将员工 ${employee.name} 从部门中移除吗？`,
        onOk: () => {
          console.log('移除员工:', employee);
          this.$message.success('员工移除成功');
          this.loadDepartmentEmployees(this.selectedDepartment.dept_code);
        }
      });
    }
  }
};
</script>

<style scoped>
.account-management {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

/* 页面标题 */
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.title-section h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.title-section p {
  color: #6b7280;
  margin: 0;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover {
  background: #f9fafb;
}

.btn-primary {
  background: #1f2937;
  color: white;
  border-color: #1f2937;
}

.btn-primary:hover {
  background: #374151;
}

.btn-outline {
  background: white;
  color: #374151;
  border-color: #d1d5db;
}

/* 搜索区域 */
.search-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-bar {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-input {
  flex: 1;
  max-width: 400px;
}

.role-filter {
  width: 150px;
}

/* 用户列表 */
.user-list-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.list-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.user-count {
  color: #6b7280;
  font-size: 14px;
}

/* 表格样式 */
.user-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr 1fr 1fr;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.header-cell {
  padding: 12px 16px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.table-body {
  background: white;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr 1fr 1fr;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  padding: 16px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
}

/* 用户信息 */
.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #1f2937;
}

.user-email {
  color: #6b7280;
  font-size: 12px;
}

/* 角色和部门 */
.role-badge, .department-name {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

/* 状态 */
.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #1f2937;
  color: white;
}

.status-badge.inactive {
  background: #f3f4f6;
  color: #6b7280;
}

/* 权限 */
.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.permission-tag {
  background: #e5e7eb;
  color: #374151;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.more-permissions {
  background: #d1d5db;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

/* 操作按钮 */
.actions-cell {
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 4px;
}

.action-btn:hover {
  background: #f9fafb;
}

.edit-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.delete-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
}

/* 模态框样式 */
.modal-content {
  padding: 0;
}

.modal-subtitle {
  color: #6b7280;
  margin-bottom: 24px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.loading-row, .empty-row {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.loading-text, .empty-text {
  font-size: 14px;
}

/* 角色管理模态框 */
.role-section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.role-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.role-table .table-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr;
  background: #f9fafb;
}

.role-table .table-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr;
}

/* 部门管理模态框 */
.department-modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfc;
}

.header-info h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.header-info p {
  color: #6b7280;
  margin: 0;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.department-management-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  flex: 1;
  overflow: hidden;
}

.department-list-section {
  border-right: 1px solid #e5e7eb;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.section-stats {
  display: flex;
  gap: 16px;
}

.stat-item {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.department-table-container {
  flex: 1;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.department-table {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.department-table .table-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 1;
}

.department-table .table-body {
  flex: 1;
  overflow-y: auto;
}

.department-table .table-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr 1fr;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s;
}

.department-table .table-row:hover {
  background: #f9fafb;
}

.department-table .table-row.active {
  background: #eff6ff;
  border-left: 3px solid #3b82f6;
}

.department-info {
  display: flex;
  flex-direction: column;
}

.department-name {
  font-weight: 500;
  color: #1f2937;
  font-size: 14px;
}

.department-desc {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.code-badge {
  background: #e5e7eb;
  color: #374151;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.leader-name {
  font-size: 13px;
  color: #374151;
}

.member-count {
  font-size: 13px;
  color: #3b82f6;
  font-weight: 500;
}

.create-time {
  font-size: 12px;
  color: #6b7280;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* 成员管理区域 */
.department-members-section {
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.members-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.members-title h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.dept-name {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
}

.members-actions {
  display: flex;
  gap: 8px;
}

.members-content {
  flex: 1;
  overflow: hidden;
}

.members-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.members-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.members-grid {
  flex: 1;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding-right: 8px;
}

.member-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.member-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  margin: 0 auto 12px auto;
}

.member-info {
  text-align: center;
}

.member-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 4px;
}

.member-role {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
}

.member-phone {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.member-status {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.member-status.active {
  background: #dcfce7;
  color: #16a34a;
}

.member-status.inactive {
  background: #fef2f2;
  color: #dc2626;
}

.member-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.member-card:hover .member-actions {
  opacity: 1;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-icon {
  margin-bottom: 16px;
}

.empty-state .empty-icon .anticon {
  font-size: 48px;
  color: #d1d5db;
}

.empty-state h5 {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.loading-state .anticon {
  margin-bottom: 16px;
  color: #3b82f6;
}

.members-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.members-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.members-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.member-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.member-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  margin-bottom: 8px;
}

.member-info {
  text-align: center;
}

.member-name {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
}

.member-role {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.member-phone {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 1px;
}

.member-status {
  font-size: 10px;
  color: #16a34a;
  margin-top: 1px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 16px;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: flex-start;
  }
  
  .search-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .user-table .table-header,
  .user-table .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .table-cell {
    padding: 8px 16px;
  }
  
  .department-management-layout {
    grid-template-columns: 1fr;
  }
  
  .department-list-section {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding-right: 0;
    padding-bottom: 24px;
  }
}
</style>